/**
 * Suggests the correct BunnyByShop market (HU vs EU) based on Shopify
 * browsing_context_suggestions + domain mapping.
 */
(function () {
  const modal = document.getElementById('MarketSuggestModal');
  const configEl = document.getElementById('MarketSuggestConfig');
  if (!modal || !configEl || window.Shopify?.designMode) return;

  // Extra guard for AI/utility pages if modal somehow still mounts
  const path = (window.location.pathname || '').toLowerCase();
  if (
    /\/pages\/(llms|agents|ai-catalog|chatgpt-gpt|llms-txt)\/?$/.test(path) ||
    path === '/llms.txt' ||
    path === '/llms-full.txt'
  ) {
    return;
  }

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  const STORAGE_KEY = config.storageKey || 'bbl-market-suggest-v1';
  const DISMISS_DAYS = 14;

  function readDismissed() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function writeDismissed(payload) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      /* ignore */
    }
  }

  function isDismissedFor(detectedCountry) {
    const data = readDismissed();
    if (!data) return false;
    if (data.forever) return true;
    if (data.country && data.country !== detectedCountry) return false;
    if (!data.until) return false;
    return Date.now() < data.until;
  }

  function dismiss(detectedCountry) {
    writeDismissed({
      country: detectedCountry,
      until: Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000,
    });
    hide();
  }

  function isHuHost(host) {
    return (config.huHosts || []).includes(host);
  }

  function isEuHost(host) {
    return host === config.euHost || host === `www.${config.euHost}`;
  }

  function buildTargetUrl(origin, country) {
    const url = new URL(origin);
    url.pathname = window.location.pathname || '/';
    url.search = window.location.search || '';
    url.hash = window.location.hash || '';
    if (country) url.searchParams.set('country', country);
    // Keep language aligned with market defaults
    if (country === 'HU') url.searchParams.set('locale', 'hu');
    else url.searchParams.set('locale', 'en');
    return url.toString();
  }

  function recommend(detectedCountry) {
    const eu = config.euCountries || [];
    if (detectedCountry === 'HU') {
      return {
        kind: 'hu',
        country: 'HU',
        origin: config.huOrigin,
        currency: 'HUF',
        locale: 'hu',
      };
    }
    if (eu.includes(detectedCountry)) {
      return {
        kind: 'eu',
        country: detectedCountry,
        origin: config.euOrigin,
        currency: 'EUR',
        locale: 'en',
      };
    }
    return null;
  }

  function needsSwitch(rec, detectedCountry) {
    if (!rec) return false;
    const host = window.location.hostname;
    const currentCountry = (window.Shopify && window.Shopify.country) || config.currentCountry;

    if (rec.kind === 'hu') {
      if (isEuHost(host)) return true;
      if (isHuHost(host) && currentCountry && currentCountry !== 'HU') return true;
      return false;
    }

    // EU recommendation
    if (isHuHost(host)) return true;
    if (isEuHost(host) && currentCountry && currentCountry !== detectedCountry) return true;
    return false;
  }

  function unescapeHtml(value) {
    if (!value || typeof value !== 'string') return value;
    if (!/[&][#a-zA-Z0-9]+;/.test(value)) return value;
    const el = document.createElement('textarea');
    el.innerHTML = value;
    return el.value;
  }

  function fillCopy(rec, detectedName) {
    const i18n = config.i18n || {};
    const title = document.getElementById('MarketSuggestTitle');
    const text = document.getElementById('MarketSuggestText');
    const btn = document.getElementById('MarketSuggestSwitch');
    const meta = document.getElementById('MarketSuggestMeta');

    if (rec.kind === 'hu') {
      if (title) title.textContent = unescapeHtml(i18n.titleHu) || title.textContent;
      if (text) text.textContent = unescapeHtml(i18n.textHu) || text.textContent;
      if (btn) btn.textContent = unescapeHtml(i18n.switchHu) || btn.textContent;
    } else {
      if (title) title.textContent = unescapeHtml(i18n.titleEu) || title.textContent;
      if (text) text.textContent = unescapeHtml(i18n.textEu) || text.textContent;
      if (btn) btn.textContent = unescapeHtml(i18n.switchEu) || btn.textContent;
    }

    if (meta) {
      const detectedLabel = unescapeHtml(i18n.detected || 'Detected: {{ country }}').replace(
        '{{ country }}',
        detectedName || rec.country
      );
      const currentLabel = unescapeHtml(i18n.current || 'Current: {{ country }} · {{ currency }}')
        .replace('{{ country }}', config.currentCountryName || config.currentCountry || '')
        .replace('{{ currency }}', config.currentCurrency || '');
      meta.hidden = false;
      meta.replaceChildren();
      const detectedEl = document.createElement('span');
      detectedEl.textContent = detectedLabel;
      const currentEl = document.createElement('span');
      currentEl.textContent = currentLabel;
      meta.append(detectedEl, currentEl);
    }
  }

  function show() {
    modal.hidden = false;
    document.documentElement.classList.add('market-suggest-open');
    const closeBtn = modal.querySelector('.market-suggest__close');
    closeBtn?.focus({ preventScroll: true });
  }

  function hide() {
    modal.hidden = true;
    document.documentElement.classList.remove('market-suggest-open');
  }

  async function init() {
    let detectedCountry = null;
    let detectedName = null;

    try {
      const res = await fetch('/browsing_context_suggestions.json', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        detectedCountry = data?.detected_values?.country?.handle || null;
        detectedName = data?.detected_values?.country?.name || data?.detected_values?.country_name || null;

        // Prefer official Shopify suggestion redirect when present
        const suggestion = Array.isArray(data?.suggestions) ? data.suggestions[0] : null;
        if (suggestion?.parts?.country?.handle) {
          detectedCountry = suggestion.parts.country.handle;
        }
      }
    } catch (e) {
      return;
    }

    if (!detectedCountry) return;
    if (isDismissedFor(detectedCountry)) return;

    const rec = recommend(detectedCountry);
    if (!needsSwitch(rec, detectedCountry)) return;

    fillCopy(rec, detectedName);
    show();

    const switchBtn = document.getElementById('MarketSuggestSwitch');
    switchBtn?.addEventListener('click', () => {
      writeDismissed({ country: detectedCountry, until: Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000 });
      window.location.assign(buildTargetUrl(rec.origin, rec.country));
    });

    modal.querySelectorAll('[data-market-dismiss]').forEach((el) => {
      el.addEventListener('click', () => dismiss(detectedCountry));
    });

    document.addEventListener('keydown', (evt) => {
      if (evt.key === 'Escape' && !modal.hidden) dismiss(detectedCountry);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
